import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Users, Clock, Calendar, MessageSquare, Plus, Bell } from "lucide-react";
import { format, startOfWeek, endOfWeek, addDays, isSameDay, isToday } from "date-fns";
import { sv } from "date-fns/locale";
import UserAvatar from "@/components/shared/UserAvatar";
import { cn } from "@/lib/utils";

export default function CoachDashboard() {
  const { t } = useTranslation("coach");
  const { user, profile } = useAuth();
  const navigate = useNavigate();
  const firstName = profile?.full_name?.split(" ")[0] ?? "";

  const today = useMemo(() => new Date(), []);
  const todayStr = format(today, "yyyy-MM-dd");
  const weekStart = startOfWeek(today, { weekStartsOn: 1 });
  const weekEnd = endOfWeek(today, { weekStartsOn: 1 });
  const weekStartStr = format(weekStart, "yyyy-MM-dd");
  const weekEndStr = format(weekEnd, "yyyy-MM-dd");

  const [selectedDay, setSelectedDay] = useState<Date>(today);

  // ── My groups ──
  const { data: groups = [], isLoading: groupsLoading } = useQuery({
    queryKey: ["coach-groups", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("training_groups")
        .select("id, name, level, max_players, color, group_members(id)")
        .eq("coach_id", user!.id);
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!user?.id,
  });

  // ── This week's sessions ──
  const { data: weekSessions = [], isLoading: sessionsLoading } = useQuery({
    queryKey: ["coach-week-sessions", user?.id, weekStartStr],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("training_sessions")
        .select("id, session_date, start_time, duration_minutes, status, group_id, court_id, training_groups(name), courts(name), session_attendance(id, rsvp_status)")
        .eq("coach_id", user!.id)
        .gte("session_date", weekStartStr)
        .lte("session_date", weekEndStr)
        .neq("status", "cancelled")
        .order("session_date")
        .order("start_time");
      if (error) throw error;
      return (data ?? []) as any[];
    },
    enabled: !!user?.id,
  });

  // ── Week days for pill strip ──
  const weekDays = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => {
      const d = addDays(weekStart, i);
      const sessions = weekSessions.filter((s: any) => s.session_date === format(d, "yyyy-MM-dd"));
      return { date: d, count: sessions.length };
    });
  }, [weekStart, weekSessions]);

  // ── Sessions for selected day ──
  const daySessions = useMemo(() => {
    const dayStr = format(selectedDay, "yyyy-MM-dd");
    return weekSessions.filter((s: any) => s.session_date === dayStr);
  }, [weekSessions, selectedDay]);

  // ── Today's sessions (for hero card) ──
  const todaySessions = weekSessions.filter((s: any) => s.session_date === todayStr);

  // ── Unread cancellation notifications ──
  const { data: unreadCancellations = 0 } = useQuery({
    queryKey: ["coach-unread-cancellations", user?.id],
    queryFn: async () => {
      const { count } = await supabase
        .from("notifications")
        .select("*", { count: "exact", head: true })
        .eq("user_id", user!.id)
        .eq("type", "rsvp_cancelled")
        .eq("read", false);
      return count ?? 0;
    },
    enabled: !!user?.id,
    refetchInterval: 30000,
  });

  const isLoading = groupsLoading || sessionsLoading;

  if (isLoading) {
    return (
      <div className="space-y-4 pb-20 max-w-lg mx-auto">
        <Skeleton className="h-20" />
        <Skeleton className="h-32" />
        <Skeleton className="h-48" />
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-20 max-w-lg lg:max-w-6xl mx-auto">
      {/* ── Header — desktop: wide with integrated actions ── */}
      <div className="flex items-center gap-4">
        <UserAvatar avatarUrl={profile?.avatar_url} fullName={profile?.full_name} size="lg" className="lg:h-14 lg:w-14" />
        <div className="flex-1">
          <h1 className="text-xl lg:text-2xl font-bold">{t("greeting", { name: firstName })}</h1>
          <p className="text-sm text-muted-foreground">{format(today, "EEEE d MMMM", { locale: sv })}</p>
        </div>
        {/* Desktop: inline quick actions */}
        <div className="hidden lg:flex gap-2">
          <Button variant="outline" size="sm" className="gap-1.5" onClick={() => navigate("/coach/availability")}>
            <Clock className="h-3.5 w-3.5" /> {t("mySchedule")}
          </Button>
          <Button variant="outline" size="sm" className="gap-1.5" onClick={() => navigate("/manager/training")}>
            <Plus className="h-3.5 w-3.5" /> {t("createSession")}
          </Button>
        </div>
        {unreadCancellations > 0 && (
          <Button variant="ghost" size="icon" className="relative shrink-0" onClick={() => navigate("/notifications")}>
            <Bell className="h-5 w-5" />
            <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-orange-500 text-[9px] font-bold text-white px-1">
              {unreadCancellations}
            </span>
          </Button>
        )}
      </div>

      {/* ── Sessions row: Today + This week side by side on desktop ── */}
      <div className="lg:grid lg:grid-cols-2 lg:gap-6 space-y-6 lg:space-y-0">
        {/* Today's sessions */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm uppercase tracking-wide text-muted-foreground">{t("todaySessions")}</CardTitle>
          </CardHeader>
          <CardContent>
            {todaySessions.length === 0 ? (
              <p className="text-center text-muted-foreground text-sm py-6">{t("noSessionsToday")}</p>
            ) : (
              <div className="space-y-2">
                {todaySessions.map((s: any) => (
                  <SessionCard key={s.id} session={s} t={t} navigate={navigate} />
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* This week */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm uppercase tracking-wide text-muted-foreground">{t("thisWeek")}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-1.5 overflow-x-auto scrollbar-hide pb-2">
              {weekDays.map(({ date, count }) => {
                const isSelected = isSameDay(date, selectedDay);
                const isTodayDate = isToday(date);
                return (
                  <button
                    key={date.toISOString()}
                    onClick={() => setSelectedDay(date)}
                    className={cn(
                      "flex-shrink-0 flex flex-col items-center px-3 py-2 rounded-lg text-xs min-w-[48px] transition-colors",
                      isSelected ? "bg-primary text-primary-foreground" : "bg-muted hover:bg-muted/80"
                    )}
                  >
                    <span className={cn("text-[10px]", isSelected ? "text-primary-foreground/80" : "text-muted-foreground")}>
                      {isTodayDate ? t("today") : format(date, "EEE", { locale: sv })}
                    </span>
                    <span className="font-bold text-base leading-tight">{format(date, "d")}</span>
                    {count > 0 && (
                      <span className={cn("text-[9px] font-medium mt-0.5", isSelected ? "text-primary-foreground/80" : "text-primary")}>
                        {count} {t("sessions")}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
            {daySessions.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">{t("noSessionsThisWeek")}</p>
            ) : (
              <div className="space-y-2 mt-2">
                {daySessions.map((s: any) => (
                  <SessionCard key={s.id} session={s} t={t} navigate={navigate} />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ── My groups — responsive grid ── */}
      <div>
        <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">{t("myGroups")}</h2>
        {groups.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center text-muted-foreground text-sm">{t("noGroups")}</CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {groups.map((g: any) => {
              const memberCount = g.group_members?.length ?? 0;
              // Find next session for this group
              const nextSession = weekSessions.find((s: any) => s.group_id === g.id && s.session_date >= todayStr);
              return (
                <Card
                  key={g.id}
                  className="cursor-pointer hover:shadow-md transition-shadow border-l-4"
                  style={{ borderLeftColor: g.color || "#6366f1" }}
                  onClick={() => navigate(`/coach/groups/${g.id}`)}
                >
                  <CardContent className="p-4 space-y-2">
                    <p className="font-semibold text-sm truncate">{g.name}</p>
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <Users className="h-3 w-3" />
                      <span>{memberCount}/{g.max_players ?? "∞"}</span>
                    </div>
                    {g.level && <Badge variant="outline" className="text-[10px]">{g.level}</Badge>}
                    {nextSession && (
                      <p className="text-[10px] text-muted-foreground flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {format(new Date(nextSession.session_date), "EEE d/M", { locale: sv })} {nextSession.start_time?.slice(0, 5)}
                      </p>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Mobile-only quick actions ── */}
      <div className="lg:hidden">
        <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-2">{t("quickActions")}</h2>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" size="sm" className="flex-1 gap-1.5" onClick={() => navigate("/coach/availability")}>
            <Clock className="h-3.5 w-3.5" /> {t("mySchedule")}
          </Button>
          <Button variant="outline" size="sm" className="flex-1 gap-1.5" onClick={() => navigate("/manager/training")}>
            <Plus className="h-3.5 w-3.5" /> {t("createSession")}
          </Button>
          <Button variant="outline" size="sm" className="flex-1 gap-1.5" onClick={() => {
            if (groups.length > 0) navigate(`/coach/groups/${groups[0].id}`);
          }}>
            <MessageSquare className="h-3.5 w-3.5" /> {t("messageGroup")}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Session card sub-component ──
function SessionCard({ session, t, navigate }: { session: any; t: any; navigate: any }) {
  const attending = (session.session_attendance ?? []).filter((a: any) => a.rsvp_status === "attending").length;
  // /coach/sessions/:id has no route (was a 404) — the session's group detail
  // page is the real destination.
  return (
    <Card className="cursor-pointer hover:shadow-sm transition-shadow" onClick={() => navigate(`/coach/groups/${session.group_id}`)}>
      <CardContent className="p-3 flex items-center gap-3">
        <div className="text-center shrink-0 w-14">
          <p className="text-lg font-bold leading-tight">{session.start_time?.slice(0, 5)}</p>
          <p className="text-[10px] text-muted-foreground">{session.duration_minutes} min</p>
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm truncate">{(session.training_groups as any)?.name ?? "—"}</p>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            {(session.courts as any)?.name && (
              <span className="flex items-center gap-0.5">
                <Calendar className="h-3 w-3" /> {(session.courts as any).name}
              </span>
            )}
            <span className="flex items-center gap-0.5">
              <Users className="h-3 w-3" /> {attending} {t("enrolled")}
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
